import React from 'react';
import ReactDOM from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { ApolloClient, InMemoryCache, ApolloProvider } from '@apollo/client';
import ProductConfigurator from './ProductConfigurator/productConfigurator';

/**
 * Standalone entry point for Product Configurator
 * This creates a global function that can be called from Magento/Hyva templates
 */

// Initialize global stubs for PWA-Studio functions that may not exist in standalone mode
console.log('Configurator v1.0.1 - Initializing global stubs');

if (typeof globalThis._ydWidgetsHasPermission === 'undefined') {
    console.log('Creating stub for _ydWidgetsHasPermission');
    globalThis._ydWidgetsHasPermission = function(type, code, position, place) {
        // Default: allow all widgets in standalone mode
        return true;
    };
}

// Initialize Apollo Client for GraphQL
const createApolloClient = (graphqlEndpoint) => {
  return new ApolloClient({
    uri: graphqlEndpoint || '/graphql',
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });
};

// Global mount function
window.mountProductConfigurator = function(elementId, options = {}) {
  const element = document.getElementById(elementId);
  
  if (!element) {
    console.error(`Element with id "${elementId}" not found`);
    return;
  }

  const {
    product,
    productId,
    graphqlEndpoint = '/graphql',
    ...otherProps
  } = options;

  // Debug logging
  console.log('mountProductConfigurator called with:', {
    elementId,
    product,
    productId,
    graphqlEndpoint,
    otherProps
  });

  if (!product) {
    console.error('Product data is required but not provided');
    return;
  }
  
  console.log('Product structure check:', {
    hasProduct: !!product,
    productKeys: Object.keys(product),
    productId: product.id,
    productSku: product.sku,
    hasConfigData: !!product.configurator_data
  });

  // Create Apollo client instance
  const client = createApolloClient(graphqlEndpoint);
  
  console.log('About to render ProductConfigurator with product:', product);

  // Render the configurator
  ReactDOM.render(
    <BrowserRouter>
      <ApolloProvider client={client}>
        <ProductConfigurator 
          product={product}
          productId={productId}
          {...otherProps}
        />
      </ApolloProvider>
    </BrowserRouter>,
    element
  );
  
  console.log('ReactDOM.render completed');

  // Return unmount function
  return () => {
    ReactDOM.unmountComponentAtNode(element);
  };
};

// Global unmount function
window.unmountProductConfigurator = function(elementId) {
  const element = document.getElementById(elementId);
  if (element) {
    ReactDOM.unmountComponentAtNode(element);
  }
};

// Auto-mount if data-configurator attribute exists
document.addEventListener('DOMContentLoaded', function() {
  const configuratorElements = document.querySelectorAll('[data-auto-mount-configurator]');
  
  configuratorElements.forEach(element => {
    const productData = element.dataset.product ? JSON.parse(element.dataset.product) : null;
    const productId = element.dataset.productId ? parseInt(element.dataset.productId, 10) : null;
    const graphqlEndpoint = element.dataset.graphqlEndpoint;

    if (productData && element.id) {
      window.mountProductConfigurator(element.id, {
        product: productData,
        productId: productId,
        graphqlEndpoint: graphqlEndpoint
      });
    }
  });
});

export { createApolloClient };
export default window.mountProductConfigurator;
